/**
 * DynamicForm — renders any FormSchema as a multi-step wizard.
 *   <DynamicForm schema={...} onSubmit={(data, files) => ...} />
 *
 * Field types supported: text, email, phone, number, textarea, select,
 * radio, checkbox, switch, date, file, header (display-only),
 * user_picker (live-filtered dropdown of active users — see UserPicker
 * below for config shape).
 *
 * Per-step "Next" runs validation for that step only. Files are kept in a
 * separate state and submitted alongside JSON data via FormData.
 */
import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import api from '../utils/api';
import { useSettings } from '../store/settings';

/** When a parent field changes, clear dependent pickers (internal Book Now parity). */
const CLEAR_ON_CHANGE = {
    franchiseType: ['territory', 'franchiseState', 'franchiseDistrict', 'franchiseCity', 'franchisePin'],
    franchiseState: ['franchiseDistrict', 'franchiseCity', 'franchisePin', 'territory'],
    franchiseDistrict: ['franchiseCity', 'franchisePin'],
    franchiseCity: ['franchisePin'],
    personalState: ['personalDistrict'],
};

const widthClass = { full: 'sm:col-span-3', half: 'sm:col-span-2 lg:col-span-1', third: 'sm:col-span-1' };
const widthGrid = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3';

export default function DynamicForm({ schema, onSubmit, busy }) {
    /* Strip archived fields — they're kept in the schema for historical
       submission rendering but should never appear on the sales-facing form. */
    const steps = (schema?.steps || []).map(st => ({
        ...st,
        fields: (st.fields || []).filter(f => !f.isArchived),
    }));
    const [stepIdx, setStepIdx] = useState(0);
    const [data, setData] = useState({});
    const [files, setFiles] = useState({});
    const [errors, setErrors] = useState({});

    const step = steps[stepIdx];
    const isLast = stepIdx === steps.length - 1;

    const setVal = (key, val) => {
        setData(d => {
            const next = { ...d, [key]: val };
            for (const dep of CLEAR_ON_CHANGE[key] || []) delete next[dep];
            return next;
        });
        setErrors(e => {
            const n = { ...e };
            delete n[key];
            for (const dep of CLEAR_ON_CHANGE[key] || []) delete n[dep];
            return n;
        });
    };
    const setFile = (key, file) => setFiles(f => ({ ...f, [key]: file }));

    const validateStep = () => {
        const e = {};
        for (const f of step.fields || []) {
            if (f.type === 'header') continue;
            if (!isVisible(f, data)) continue;
            const v = f.type === 'file' ? files[f.key] : data[f.key];
            if (f.required && (v === undefined || v === null || v === '' || v === false)) {
                e[f.key] = `${f.label} is required`;
                continue;
            }
            if (typeof v === 'string') {
                if (f.validation?.minLength && v.length < f.validation.minLength) e[f.key] = `Min ${f.validation.minLength} chars`;
                if (f.validation?.maxLength && v.length > f.validation.maxLength) e[f.key] = `Max ${f.validation.maxLength} chars`;
                if (f.validation?.pattern && f.type !== 'phone') {
                    try { if (!new RegExp(f.validation.pattern).test(v)) e[f.key] = f.validation.patternHint || 'Invalid format'; }
                    catch { /* bad regex from admin — ignore */ }
                }
            }
            if (f.type === 'number') {
                const n = Number(v);
                if (v !== undefined && v !== '' && !Number.isNaN(n)) {
                    if (f.validation?.min !== undefined && n < f.validation.min) e[f.key] = `Min ${f.validation.min}`;
                    if (f.validation?.max !== undefined && n > f.validation.max) e[f.key] = `Max ${f.validation.max}`;
                }
            }
            if (f.type === 'email' && v) {
                if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) e[f.key] = 'Invalid email';
            }
            if (f.type === 'phone' && v) {
                const digits = String(v).replace(/\D/g, '');
                if (!/^\d{10}$/.test(digits)) e[f.key] = 'Enter a valid 10-digit mobile number';
            }
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const next = () => { if (validateStep()) setStepIdx(i => Math.min(i + 1, steps.length - 1)); };
    const back = () => setStepIdx(i => Math.max(i - 1, 0));
    const submit = (e) => {
        e?.preventDefault();
        if (!validateStep()) return;
        onSubmit?.(data, files);
    };

    if (!step) return <div className="text-ink-500">No steps defined.</div>;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
            {/* Step rail */}
            <div className="ag-card p-4 lg:sticky lg:top-20 h-fit">
                <h3 className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-3">Steps</h3>
                <ol className="space-y-2">
                    {steps.map((s, i) => {
                        const active = i === stepIdx;
                        const done = i < stepIdx;
                        return (
                            <li key={i} className={`flex items-start gap-3 cursor-pointer rounded-lg p-2 transition
                                ${active ? 'bg-brand-teal-50' : 'hover:bg-ink-100'}`}
                                onClick={() => i <= stepIdx && setStepIdx(i)}>
                                <div className={`shrink-0 h-7 w-7 rounded-full grid place-items-center text-xs font-bold border-2
                                    ${done ? 'bg-brand-teal text-white border-brand-teal'
                                        : active ? 'border-brand-teal text-brand-teal-700'
                                            : 'border-ink-300 text-ink-500'}`}>
                                    {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                                </div>
                                <div className={`text-sm ${active ? 'text-brand-teal-700 font-semibold' : 'text-ink-700'}`}>
                                    {s.title}
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </div>

            {/* Form panel */}
            <form onSubmit={submit} className="space-y-4">
                <div className="ag-card p-5">
                    <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold">Step {stepIdx + 1} of {steps.length}</div>
                    <h2 className="text-xl font-extrabold mt-1">{step.title}</h2>
                    {step.description && <p className="text-sm text-ink-500 mt-1">{step.description}</p>}

                    <div className={`${widthGrid} mt-5`}>
                        {(step.fields || []).map(f => {
                            if (!isVisible(f, data)) return null;
                            return (
                                <div key={f.key} className={widthClass[f.width || 'full']}>
                                    <Render field={f} value={data[f.key]} onChange={v => setVal(f.key, v)}
                                        file={files[f.key]} onFile={v => setFile(f.key, v)}
                                        error={errors[f.key]} data={data} />
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex justify-between">
                    <button type="button" className="ag-btn-ghost" onClick={back} disabled={stepIdx === 0}>
                        <ArrowLeft className="h-4 w-4" /> Back
                    </button>
                    {!isLast ? (
                        <button type="button" className="ag-btn-primary" onClick={next}>
                            Next <ArrowRight className="h-4 w-4" />
                        </button>
                    ) : (
                        <button className="ag-btn-primary" disabled={busy}>
                            {busy ? 'Submitting…' : 'Submit'}
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
}

export function isFieldVisible(field, data) {
    if (!field.visibleIf?.key) return true;
    const v = data[field.visibleIf.key];
    const eq = field.visibleIf.equals;
    if (Array.isArray(eq)) return eq.includes(v);
    return v === eq;
}

function isVisible(field, data) {
    return isFieldVisible(field, data);
}

export { CLEAR_ON_CHANGE };

function Render({ field, value, onChange, file, onFile, error, data }) {
    const labelEl = (field.type !== 'header' && field.type !== 'switch' && field.type !== 'checkbox') && (
        <label className="ag-label">{field.label}{field.required && ' *'}</label>
    );
    const hint = field.helpText && <div className="text-[11px] text-ink-500 mt-1">{field.helpText}</div>;
    const errEl = error && <div className="text-[11px] text-brand-red mt-1">{error}</div>;

    if (field.type === 'header') {
        return <div className="border-b border-ink-100 pb-2"><h4 className="font-bold">{field.label}</h4>{field.helpText && <p className="text-xs text-ink-500 mt-0.5">{field.helpText}</p>}</div>;
    }
    if (field.type === 'textarea') {
        return <div>{labelEl}<textarea className="ag-input min-h-[90px]" placeholder={field.placeholder} value={value ?? ''} onChange={e => onChange(e.target.value)} />{hint}{errEl}</div>;
    }
    if (field.type === 'select') {
        if (field.optionsSource) {
            return <div>{labelEl}
                <DynamicSelect field={field} value={value} onChange={onChange} data={data} />
                {hint}{errEl}
            </div>;
        }
        return <div>{labelEl}<select className="ag-input" value={value ?? ''} onChange={e => onChange(e.target.value)}>
            <option value="">{field.placeholder || 'Select…'}</option>
            {(field.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>{hint}{errEl}</div>;
    }
    if (field.type === 'user_picker') {
        return <div>{labelEl}
            <UserPicker field={field} value={value} onChange={onChange} data={data} />
            {hint}{errEl}
        </div>;
    }
    if (field.type === 'radio') {
        return <div>{labelEl}<div className="flex flex-wrap gap-3 mt-1">
            {(field.options || []).map(o => (
                <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name={field.key} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} />
                    {o.label}
                </label>
            ))}
        </div>{hint}{errEl}</div>;
    }
    if (field.type === 'checkbox') {
        return <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
            {field.label}{field.required && ' *'}
        </label>;
    }
    if (field.type === 'switch') {
        return (
            <div>
                <label className="flex items-start gap-3 py-1.5 cursor-pointer">
                    <span className={`mt-0.5 w-9 h-5 rounded-full transition relative ${value ? 'bg-brand-teal' : 'bg-ink-300'}`}>
                        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition ${value ? 'left-[18px]' : 'left-0.5'}`} />
                        <input type="checkbox" className="sr-only" checked={!!value} onChange={e => onChange(e.target.checked)} />
                    </span>
                    <span className="text-sm font-medium">{field.label}{field.required && ' *'}</span>
                </label>
                {hint}{errEl}
            </div>
        );
    }
    if (field.type === 'file') {
        return <div>{labelEl}
            <label className="ag-input flex items-center justify-between cursor-pointer text-xs">
                <span className="truncate">{file ? file.name : (field.placeholder || 'Choose file…')}</span>
                <input type="file" className="hidden" accept={field.accept || 'image/*,.pdf'} onChange={e => onFile(e.target.files?.[0] || null)} />
                <span className="ag-btn-ghost text-[11px] py-1 px-2">Browse</span>
            </label>{hint}{errEl}</div>;
    }
    if (field.type === 'date') {
        return <div>{labelEl}<input className="ag-input" type="date" value={value ?? ''} onChange={e => onChange(e.target.value)} />{hint}{errEl}</div>;
    }
    if (field.type === 'number') {
        return <div>{labelEl}<input className="ag-input" type="number" placeholder={field.placeholder} value={value ?? ''} onChange={e => onChange(e.target.value)} />{hint}{errEl}</div>;
    }
    if (field.type === 'phone') {
        /* Same restriction as signup — digits only, max 10. */
        return (
            <div>{labelEl}
                <input
                    className="ag-input"
                    type="tel"
                    inputMode="numeric"
                    placeholder={field.placeholder || '10-digit mobile number'}
                    value={value ?? ''}
                    onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
                />
                {hint}{errEl}
            </div>
        );
    }
    // text / email (default)
    return <div>{labelEl}<input className="ag-input" type={field.type} placeholder={field.placeholder} value={value ?? ''} onChange={e => onChange(e.target.value)} />{hint}{errEl}</div>;
}

function resolveOptionsSource(field, data) {
    const src = field.optionsSource;
    if (!src) return null;
    if (src.resolver === 'franchise_territory') {
        if (data.franchiseType === 'Master Franchise') return { kind: 'master_franchise_territories' };
        if (data.franchiseType === 'Daewoo District Partner Franchise') {
            return { kind: 'ddp_territories', dependsOn: 'franchiseState' };
        }
        return null;
    }
    if (src.resolver === 'franchise_state') {
        if (data.franchiseType === 'Daewoo District Partner Franchise') return { kind: 'ddp_states' };
        if (data.franchiseType === 'Signature') return { kind: 'signature_states', franchiseType: 'signature' };
        if (data.franchiseType === 'SignatureFico') return { kind: 'signature_states', franchiseType: 'signatureFico' };
        return { kind: 'indian_states' };
    }
    if (src.resolver === 'franchise_district') {
        if (data.franchiseType === 'Signature') return { kind: 'signature_districts', franchiseType: 'signature', dependsOn: 'franchiseState' };
        if (data.franchiseType === 'SignatureFico') return { kind: 'signature_districts', franchiseType: 'signatureFico', dependsOn: 'franchiseState' };
        return { kind: 'indian_districts', dependsOn: 'franchiseState' };
    }
    if (src.resolver === 'franchise_city') {
        if (data.franchiseType === 'Signature') return { kind: 'signature_cities', franchiseType: 'signature', dependsOn: 'franchiseDistrict' };
        if (data.franchiseType === 'SignatureFico') return { kind: 'signature_cities', franchiseType: 'signatureFico', dependsOn: 'franchiseDistrict' };
        return null;
    }
    if (src.resolver === 'franchise_pin') {
        if (data.franchiseType === 'Signature') return { kind: 'signature_pins', franchiseType: 'signature', dependsOn: 'franchiseCity' };
        if (data.franchiseType === 'SignatureFico') return { kind: 'signature_pins', franchiseType: 'signatureFico', dependsOn: 'franchiseCity' };
        return null;
    }
    return src;
}

export function DynamicSelect({ field, value, onChange, data }) {
    const resolved = useMemo(() => resolveOptionsSource(field, data), [field, data]);
    const parentKey = resolved?.dependsOn;
    const parentVal = parentKey ? data?.[parentKey] : '';

    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!resolved?.kind) {
            setOptions([]);
            return;
        }
        if (parentKey && !parentVal) {
            setOptions([]);
            return;
        }

        let alive = true;
        setLoading(true);
        const load = async () => {
            try {
                let items = [];
                if (resolved.kind === 'indian_states' || resolved.kind === 'ddp_states') {
                    const path = resolved.kind === 'ddp_states'
                        ? '/reference/franchise/ddp-states'
                        : '/reference/states';
                    const r = await api.get(path);
                    items = (r.data.states || []).map(s => ({ value: s, label: s, disabled: false }));
                } else if (resolved.kind === 'signature_states') {
                    const r = await api.get(`/reference/franchise/${resolved.franchiseType}/states`);
                    items = (r.data.states || []).map(s => ({ value: s, label: s, disabled: false }));
                } else if (resolved.kind === 'indian_districts') {
                    const r = await api.get(`/reference/districts/${encodeURIComponent(parentVal)}`);
                    items = (r.data.districts || []).map(d => ({ value: d, label: d, disabled: false }));
                } else if (resolved.kind === 'signature_districts') {
                    const r = await api.get(`/reference/franchise/${resolved.franchiseType}/districts/${encodeURIComponent(data?.franchiseState || '')}`);
                    items = (r.data.districts || []).map(d => ({ value: d, label: d, disabled: false }));
                } else if (resolved.kind === 'master_franchise_territories') {
                    const r = await api.get('/reference/franchise/master-territories');
                    items = (r.data.territories || []).map(t => ({
                        value: t.value || t.territory,
                        label: t.label || t.territory,
                        disabled: Boolean(t.disabled),
                    }));
                } else if (resolved.kind === 'ddp_territories') {
                    const r = await api.get(`/reference/franchise/ddp-territories/${encodeURIComponent(parentVal)}`);
                    items = (r.data.territories || []).map(t => ({
                        value: t.value || t.territory,
                        label: t.label || t.territory,
                        disabled: Boolean(t.disabled),
                    }));
                } else if (resolved.kind === 'signature_cities') {
                    const r = await api.get(`/reference/franchise/${resolved.franchiseType}/cities/${encodeURIComponent(data?.franchiseState || '')}/${encodeURIComponent(data?.franchiseDistrict || '')}`);
                    items = (r.data.cities || []).map(c => ({
                        value: c.value || c,
                        label: c.label || c,
                        disabled: false,
                    }));
                } else if (resolved.kind === 'signature_pins') {
                    const r = await api.get(`/reference/franchise/${resolved.franchiseType}/pins/${encodeURIComponent(data?.franchiseState || '')}/${encodeURIComponent(data?.franchiseDistrict || '')}/${encodeURIComponent(data?.franchiseCity || '')}`);
                    items = (r.data.pins || []).map(p => ({
                        value: p.value || p,
                        label: p.label || p,
                        disabled: false,
                    }));
                }
                if (alive) setOptions(items);
            } catch {
                if (alive) setOptions([]);
            } finally {
                if (alive) setLoading(false);
            }
        };
        load();
        return () => { alive = false; };
    }, [resolved?.kind, parentKey, parentVal]);

    useEffect(() => {
        if (!value || loading) return;
        if (!options.some(o => o.value === value)) onChange('');
    }, [options, loading, value]); // eslint-disable-line react-hooks/exhaustive-deps

    const placeholder = loading
        ? 'Loading…'
        : parentKey && !parentVal
            ? `Select ${parentKey.replace(/([A-Z])/g, ' $1').toLowerCase()} first…`
            : field.placeholder || 'Select…';

    return (
        <select
            className="ag-input"
            value={value ?? ''}
            onChange={e => onChange(e.target.value)}
            disabled={loading || (parentKey && !parentVal) || options.length === 0}
        >
            <option value="">{placeholder}</option>
            {options.map(o => (
                <option key={o.value} value={o.value} disabled={o.disabled}>
                    {o.label}{o.disabled ? ' (unavailable)' : ''}
                </option>
            ))}
        </select>
    );
}

/**
 * UserPicker — live-filtered dropdown of active users.
 *
 * Field config (on the FormSchema entry):
 *   userPicker: {
 *     salesDesignation: 'bda' | 'bde' | 'bdm' | 'operations' | 'other',
 *     officeBranchField: 'officeBranch',   // optional — pin the picker to
 *                                           // another field's selected value
 *   }
 *
 * Behavior:
 *   • Re-fetches when officeBranch (or whatever officeBranchField points at)
 *     changes — sales user picks branch first, then the BDA dropdown filters.
 *   • If the watched field is unset, we show every user with the right
 *     designation (no office filter), so admins importing legacy data still
 *     see something.
 *   • Clears the picker's own value when the watched field changes mid-edit,
 *     so the form never holds a user from the wrong branch.
 */
function UserPicker({ field, value, onChange, data }) {
    const cfg = field.userPicker || {};
    const watchedBranchKey = cfg.officeBranchField;
    const watchedBranch = watchedBranchKey ? data?.[watchedBranchKey] : '';
    const filterByLocation = useSettings(s => Boolean(s.paymentSettings?.filterUsersByLocation));

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const requireBranchFirst = Boolean(watchedBranchKey && filterByLocation);

    /* Fetch on mount + whenever salesDesignation or office changes. */
    useEffect(() => {
        let alive = true;
        const params = {};
        if (cfg.salesDesignation) params.salesDesignation = cfg.salesDesignation;
        if (filterByLocation && watchedBranch) params.officeBranch = watchedBranch;
        setLoading(true);
        api.get('/users/picker', { params })
            .then(r => { if (alive) setItems(r.data.items || []); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [cfg.salesDesignation, watchedBranch, filterByLocation]);

    /* If the user changed the watched branch after picking a person from a
       different branch, clear the now-stale selection. We do this in a
       layout effect so the form data is consistent before validation runs. */
    useEffect(() => {
        if (!value) return;
        if (!items.some(u => u._id === value)) {
            // Only clear once we've finished loading — otherwise we'd wipe
            // the value on every fetch while options are still empty.
            if (!loading) onChange('');
        }
    }, [items, loading, value]); // eslint-disable-line react-hooks/exhaustive-deps

    const desigLabel = cfg.salesDesignation === 'other'
        ? 'business head'
        : (cfg.salesDesignation || 'matching').toUpperCase();

    const placeholder =
        loading ? 'Loading…'
            : requireBranchFirst && !watchedBranch ? `Pick ${watchedBranchKey} first…`
                : items.length === 0 ? `No ${desigLabel} users in ${watchedBranch || 'this office'}`
                    : field.placeholder || 'Select user…';

    const isEmpty = !loading
        && (!requireBranchFirst || watchedBranch)
        && items.length === 0;

    return (
        <>
            <select
                className="ag-input"
                value={value ?? ''}
                onChange={e => onChange(e.target.value)}
                disabled={loading || (requireBranchFirst && !watchedBranch) || items.length === 0}
            >
                <option value="">{placeholder}</option>
                {items.map(u => (
                    <option key={u._id} value={u._id}>
                        {u.name} · {u.email}
                    </option>
                ))}
            </select>
            {isEmpty && (
                <div className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded px-2 py-1.5 leading-snug">
                    <b>No {desigLabel} users in {watchedBranch || 'this office'}.</b>{' '}
                    An admin needs to tag at least one user as <b>{desigLabel}</b> in
                    {' '}<b>{watchedBranch || 'this branch'}</b>. Open
                    {' '}<a href="/admin/users" target="_blank" rel="noreferrer" className="underline font-semibold">Admin → Users</a>{' '}
                    → set their <b>Designation</b> + <b>Office</b> columns → click <b>Save</b>.
                </div>
            )}
        </>
    );
}
